import { useEffect, useState } from "react";
import {
  adminGetFrameworks, adminUpsertFramework, adminDeleteFramework,
  adminUploadFrameworkFile, adminDeleteFrameworkFile, formatApiErrorDetail,
} from "@/lib/api";
import {
  Plus, Trash2, Save, X, ChevronUp, ChevronDown, Eye, EyeOff,
  Upload, FileText, Wrench, Paperclip,
} from "lucide-react";
import { toast } from "sonner";

const TAG_SUGGESTIONS = ["method", "decision", "production", "diagnostic", "governance"];
const SEGMENTS = ["all", "founders", "enterprises", "entrepreneurs", "nonprofits"];

const EMPTY = {
  title: "", description: "", tag: "method", segment: "all",
  gated: true, links_to_tool: false, status: "published", order: 99, body: "",
};

// body is stored as a paragraph array; edited as newline-separated text.
function toForm(fw) {
  return { ...fw, body: Array.isArray(fw.body) ? fw.body.join("\n") : (fw.body || "") };
}
function toPayload(form) {
  return {
    ...form,
    body: String(form.body || "").split("\n").map((s) => s.trim()).filter(Boolean),
  };
}

export default function AdminFrameworks() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setItems(await adminGetFrameworks()); }
    catch { toast.error("Could not load frameworks."); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...EMPTY, order: items.length + 1 });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Title is required."); return; }
    setBusy(true);
    try {
      const res = await adminUpsertFramework(toPayload(editing));
      toast.success("Saved. The Resources page updates immediately.");
      // keep editing the same record (now with id) so a file can be attached
      setEditing((e) => ({ ...e, id: res.id, slug: res.slug }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed.");
    } finally { setBusy(false); }
  };

  const togglePublished = async (c) => {
    try {
      await adminUpsertFramework({ ...c, status: c.status === "published" ? "draft" : "published" });
      toast.success(c.status === "published" ? "Moved to draft." : "Published.");
      load();
    } catch { toast.error("Could not update."); }
  };

  const reorder = async (c, dir) => {
    const sorted = [...items].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((x) => x.id === c.id);
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const a = sorted[idx], b = sorted[swapIdx];
    try {
      await adminUpsertFramework({ ...a, order: b.order });
      await adminUpsertFramework({ ...b, order: a.order });
      load();
    } catch { toast.error("Could not reorder."); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.title}"? This cannot be undone.`)) return;
    try { await adminDeleteFramework(c.id); toast.success("Deleted."); load(); }
    catch { toast.error("Could not delete."); }
  };

  const sorted = [...items].sort((a, b) => a.order - b.order);

  return (
    <div data-testid="admin-frameworks">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="cv-kicker">Frameworks</p>
          <h1 className="cv-display text-3xl mt-4">The methods, fully editable.</h1>
          <p className="cv-body text-sm text-[#1F1B16]/65 mt-3 max-w-lg">
            Each framework is a gated download in the Resources library. Publish, reorder, edit, or attach a PDF and the page updates immediately.
          </p>
        </div>
        <button data-testid="framework-new-btn" onClick={startNew}
          className="cv-btn-primary inline-flex items-center gap-2 self-start whitespace-nowrap">
          <Plus size={15} /> New framework
        </button>
      </div>

      {editing && (
        <FrameworkForm value={editing} onChange={setEditing} onSave={save} onCancel={cancel} busy={busy} reload={load} />
      )}

      <div className="mt-8 divide-y divide-[rgba(28,27,25,0.12)] border-t border-[rgba(28,27,25,0.12)]">
        {sorted.map((c, i) => (
          <div key={c.id} data-testid="admin-framework-row" className="py-4 flex items-center gap-4">
            <div className="flex flex-col">
              <button onClick={() => reorder(c, "up")} disabled={i === 0}
                className="text-[#9A938A] hover:text-[#1F1B16] disabled:opacity-30" aria-label="Move up">
                <ChevronUp size={15} />
              </button>
              <button onClick={() => reorder(c, "down")} disabled={i === sorted.length - 1}
                className="text-[#9A938A] hover:text-[#1F1B16] disabled:opacity-30" aria-label="Move down">
                <ChevronDown size={15} />
              </button>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="cv-h2 text-sm">{c.title}</p>
                {c.links_to_tool && (
                  <span className="inline-flex items-center gap-1 text-[#515E4F]" title="Links to the Scorecard tool"><Wrench size={12} /></span>
                )}
                {c.has_file && (
                  <span className="inline-flex items-center gap-1 text-[#515E4F]" title="PDF attached"><Paperclip size={12} /></span>
                )}
              </div>
              <p className="cv-body text-xs text-[#9A938A] mt-0.5 truncate">{c.tag} · {c.description}</p>
            </div>
            <button data-testid="framework-publish-toggle" onClick={() => togglePublished(c)}
              className={`inline-flex items-center gap-1.5 cv-sans uppercase tracking-[0.1em] text-[0.6rem] px-2.5 py-1 rounded-full border transition-colors ${
                c.status === "published" ? "border-[#515E4F] text-[#515E4F]" : "border-[rgba(28,27,25,0.3)] text-[#9A938A]"
              }`}>
              {c.status === "published" ? <Eye size={12} /> : <EyeOff size={12} />}
              {c.status === "published" ? "Published" : "Draft"}
            </button>
            <button onClick={() => setEditing(toForm(c))} className="cv-link text-xs">Edit</button>
            <button onClick={() => remove(c)} className="text-[#A65A43] hover:text-[#8a4836]" aria-label="Delete">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {sorted.length === 0 && (
          <p className="cv-body text-sm text-[#9A938A] py-6">No frameworks yet.</p>
        )}
      </div>
    </div>
  );
}

function FrameworkForm({ value, onChange, onSave, onCancel, busy, reload }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!value.id) { toast.error("Save the framework first, then attach a PDF."); return; }
    setUploading(true);
    try {
      const res = await adminUploadFrameworkFile(value.id, file);
      onChange({ ...value, file_name: res.file_name, has_file: true });
      toast.success("PDF attached. It now serves on download.");
      reload && reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed.");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const removeFile = async () => {
    try {
      await adminDeleteFrameworkFile(value.id);
      onChange({ ...value, file_name: null, has_file: false });
      toast.success("PDF removed. Downloads fall back to the generated one-pager.");
      reload && reload();
    } catch { toast.error("Could not remove."); }
  };

  return (
    <div data-testid="framework-form" className="mt-8 border border-[rgba(28,27,25,0.22)] rounded-[16px] overflow-hidden">
      <div className="cv-hairline-copper" />
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Title *">
            <input data-testid="framework-title" className="cv-input"
              value={value.title} onChange={(e) => set("title", e.target.value)} />
          </Field>
          <Field label="Order">
            <input data-testid="framework-order" type="number" className="cv-input"
              value={value.order} onChange={(e) => set("order", parseInt(e.target.value || "0", 10))} />
          </Field>
        </div>
        <Field label="Description (shown on the card)">
          <textarea data-testid="framework-description" rows={2} className="cv-input resize-none"
            value={value.description} onChange={(e) => set("description", e.target.value)} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Tag">
            <input data-testid="framework-tag" className="cv-input" list="framework-tags"
              value={value.tag} onChange={(e) => set("tag", e.target.value)} />
            <datalist id="framework-tags">
              {TAG_SUGGESTIONS.map((t) => <option key={t} value={t} />)}
            </datalist>
          </Field>
          <Field label="Segment">
            <select data-testid="framework-segment" className="cv-input"
              value={value.segment} onChange={(e) => set("segment", e.target.value)}>
              {SEGMENTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Body (one paragraph per line; used for the generated PDF)">
          <textarea data-testid="framework-body" rows={6} className="cv-input resize-none"
            value={value.body} onChange={(e) => set("body", e.target.value)} />
        </Field>

        {/* Toggles */}
        <div className="flex flex-wrap items-center gap-6 pt-1">
          <Toggle testid="framework-published-toggle" on={value.status === "published"}
            onClick={() => set("status", value.status === "published" ? "draft" : "published")} label="Published" />
          <Toggle testid="framework-gated-toggle" on={value.gated}
            onClick={() => set("gated", !value.gated)} label="Email gated" />
          <Toggle testid="framework-tool-toggle" on={value.links_to_tool}
            onClick={() => set("links_to_tool", !value.links_to_tool)} label="Links to Scorecard tool" />
        </div>
        {value.links_to_tool && (
          <p className="cv-body text-xs text-[#9A938A]">Tool-linked frameworks show a "Take the Scorecard" button instead of a download.</p>
        )}

        {/* PDF upload (optional) */}
        {!value.links_to_tool && (
          <div className="border-t border-[rgba(28,27,25,0.12)] pt-4">
            <p className="cv-sans uppercase tracking-[0.12em] text-[0.66rem] text-[#1F1B16]/60">Download PDF (optional)</p>
            {value.has_file || value.file_name ? (
              <div className="mt-2 flex items-center gap-3">
                <span className="inline-flex items-center gap-2 cv-body text-sm text-[#1F1B16]/80">
                  <FileText size={15} className="text-[#515E4F]" /> {value.file_name || "Attached"}
                </span>
                <button onClick={removeFile} className="cv-link text-xs text-[#A65A43]">Remove</button>
              </div>
            ) : (
              <label className={`mt-2 inline-flex items-center gap-2 cv-btn-secondary cursor-pointer ${!value.id ? "opacity-50" : ""}`}>
                {uploading ? "Uploading..." : <><Upload size={14} /> Attach PDF</>}
                <input data-testid="framework-file-input" type="file" className="hidden"
                  disabled={!value.id || uploading} onChange={onFile} accept=".pdf" />
              </label>
            )}
            {!value.id && <p className="cv-body text-xs text-[#9A938A] mt-1.5">Save first to attach a PDF. Without one, a branded one-pager is generated from the body.</p>}
          </div>
        )}

        <div className="flex items-center gap-3 pt-1">
          <button data-testid="framework-save" onClick={onSave} disabled={busy}
            className="cv-btn-primary inline-flex items-center gap-2 disabled:opacity-60">
            <Save size={14} /> {busy ? "Saving..." : "Save framework"}
          </button>
          <button onClick={onCancel} className="cv-btn-secondary inline-flex items-center gap-2">
            <X size={14} /> Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Toggle({ on, onClick, label, testid }) {
  return (
    <button data-testid={testid} type="button" onClick={onClick} className="inline-flex items-center gap-2.5">
      <span className={`w-9 h-5 rounded-full border transition-colors relative ${on ? "bg-[#A6633C] border-[#A6633C]" : "bg-transparent border-[rgba(28,27,25,0.3)]"}`}>
        <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full transition-all ${on ? "left-4 bg-[#F6F1E9]" : "left-0.5 bg-[#9A938A]"}`} />
      </span>
      <span className="cv-sans uppercase tracking-[0.1em] text-[0.68rem] text-[#1F1B16]/70">{label}</span>
    </button>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="cv-sans uppercase tracking-[0.12em] text-[0.66rem] text-[#1F1B16]/60">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
